import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const peso = (value) => `₱${Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminFinanceBatchReport() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/finance/batches/${batchId}`)
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Unable to load batch report"))
      .finally(() => setLoading(false));
  }, [batchId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="max-w-4xl mx-auto px-4 py-20 text-center font-heading text-xl">Batch report unavailable</div>;

  const { batch, settlements } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="finance-batch-report-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="batch-report-back-btn">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Payout Batch Report</h1>
          <p className="text-muted-foreground text-sm">{batch.batch_id} · {batch.status}</p>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="rounded-full" data-testid="batch-report-print-btn">
          <Download className="w-4 h-4 mr-2" />Download / Print PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard label="Status" value={batch.status} />
        <MetricCard label="Settlements" value={batch.settlements_count} />
        <MetricCard label="Gross Amount" value={peso(batch.gross_amount)} />
        <MetricCard label="Net Payout" value={peso(batch.net_payout)} />
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Batch Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <p><span className="text-muted-foreground">Batch ID:</span> {batch.batch_id}</p>
          <p><span className="text-muted-foreground">Payout Date:</span> {batch.payout_date}</p>
          <p><span className="text-muted-foreground">Cut Off:</span> {batch.cut_off_datetime}</p>
          <p><span className="text-muted-foreground">Created At:</span> {batch.created_at}</p>
          <p><span className="text-muted-foreground">Execution Notes:</span> {batch.execution_notes || batch.notes || '—'}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Settlements in Batch</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm" data-testid="batch-report-settlements-table">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 pr-3">Booking</th>
                  <th className="py-3 pr-3">Venue</th>
                  <th className="py-3 pr-3">Gross</th>
                  <th className="py-3 pr-3">Commission</th>
                  <th className="py-3 pr-3">Net</th>
                  <th className="py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((item) => (
                  <tr key={item.booking_id} className="border-b border-slate-100">
                    <td className="py-3 pr-3">{item.booking_id}</td>
                    <td className="py-3 pr-3">{item.venue_name}</td>
                    <td className="py-3 pr-3">{peso(item.gross_amount)}</td>
                    <td className="py-3 pr-3">{peso(item.commission_amount)}</td>
                    <td className="py-3 pr-3">{peso(item.net_payout)}</td>
                    <td className="py-3">{item.payout_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Transfer File Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm" data-testid="batch-report-transfer-table">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 pr-3">Venue</th>
                  <th className="py-3 pr-3">Method</th>
                  <th className="py-3 pr-3">Beneficiary</th>
                  <th className="py-3 pr-3">Bank / Wallet</th>
                  <th className="py-3 pr-3">Amount</th>
                  <th className="py-3 pr-3">Ready</th>
                  <th className="py-3">Validation</th>
                </tr>
              </thead>
              <tbody>
                {(data.transfer_rows || []).map((row, idx) => (
                  <tr key={`${row.venue_id}-${idx}`} className="border-b border-slate-100">
                    <td className="py-3 pr-3">{row.venue_name}</td>
                    <td className="py-3 pr-3 capitalize">{String(row.payout_method).replace(/_/g, ' ')}</td>
                    <td className="py-3 pr-3">{row.beneficiary_name}</td>
                    <td className="py-3 pr-3">{row.bank_name || row.mobile_number || row.account_number || '—'}</td>
                    <td className="py-3 pr-3">{peso(row.amount)}</td>
                    <td className="py-3 pr-3">{row.ready_for_transfer ? 'Yes' : 'No'}</td>
                    <td className="py-3">{row.validation_errors?.length ? row.validation_errors.join(', ') : 'OK'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}